/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tabs/VideoTab.tsx
 🗓️ Created: 2025-01-15
 🧩 Description:
    Video tab component for Conference form - Video Behavior
────────────────────────────────────────────────────────────────*/

import { Camera } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useDebugMode } from '../../../hooks/useDebugMode';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { Toggle } from '../../../components/ui/Toggle';
import { FormBox } from '../../../components/ui/FormBox';
import type { ConferenceRoom } from '../types';

interface VideoTabProps {
  formData: ConferenceRoom;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (field: keyof ConferenceRoom, value: any) => void;
}

export function VideoTab({ formData, onChange }: VideoTabProps) {
  const { t } = useI18n();
  const { isDebugModeActive } = useDebugMode();

  const buildTooltipContent = (tooltipKey: string, debugKey?: string) => {
    const base = t(tooltipKey);
    if (!debugKey || !isDebugModeActive) {
      return base;
    }
    return `${base}\n\n${t('conferences:debug.field')}: ${t(debugKey)}`;
  };

  return (
    <div className="space-y-3">
      <FormBox
        title={t('conferences:sections.video')}
        icon={<Camera className="h-5 w-5" />}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.videoMode', 'conferences:debug.video_mode')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.videoMode')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.videoMode}
            onChange={(e) => onChange('videoMode', e.target.value as 'auto' | 'follow-speaker' | 'grid')}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          >
            <option value="auto">Auto</option>
            <option value="follow-speaker">Follow Speaker</option>
            <option value="grid">Grid</option>
          </select>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.videoLayout', 'conferences:debug.video_layout')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.videoLayout')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.videoLayout}
            onChange={(e) => onChange('videoLayout', e.target.value as '1x1' | '2x2' | '3x3' | 'custom')}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          >
            <option value="1x1">1x1</option>
            <option value="2x2">2x2</option>
            <option value="3x3">3x3</option>
            <option value="custom">Custom</option>
          </select>

          {/* Row 2 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.videoCodec', 'conferences:debug.video_codec')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.videoCodec')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.videoCodec}
            onChange={(e) => onChange('videoCodec', e.target.value as 'VP8' | 'H264' | 'VP9')}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          >
            <option value="VP8">VP8</option>
            <option value="H264">H264</option>
            <option value="VP9">VP9</option>
          </select>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.maxVideoParticipants', 'conferences:debug.max_video_members')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.maxVideoParticipants')}
            </label>
          </SimpleTooltip>
          <input
            type="number"
            value={formData.maxVideoParticipants}
            onChange={(e) => onChange('maxVideoParticipants', parseInt(e.target.value) || 9)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder="9"
            min="1"
          />

          {/* Row 3 */}
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.videoResolution', 'conferences:debug.video_resolution')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.videoResolution')}
            </label>
          </SimpleTooltip>
          <select
            value={formData.videoResolution}
            onChange={(e) => onChange('videoResolution', e.target.value as '480p' | '720p' | '1080p')}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
          >
            <option value="480p">480p</option>
            <option value="720p">720p</option>
            <option value="1080p">1080p</option>
          </select>
          <SimpleTooltip
            content={buildTooltipContent('conferences:tooltips.recordVideo', 'conferences:debug.record_video')}
            position="top"
            maxWidth="300px"
          >
            <label className="text-sm font-medium text-foreground">
              {t('conferences:fields.recordVideo')}
            </label>
          </SimpleTooltip>
          <div className="flex justify-start">
            <Toggle
              value={formData.recordVideo}
              onChange={(value) => onChange('recordVideo', value)}
            />
          </div>
        </div>
      </FormBox>
    </div>
  );
}

